// Schemas for routes-knowledge.ts (browser-safe zod). PRD §9 (knowledge, documents, shareable assets), §8.1 (retrieval).
import { z } from 'zod';
import { DocumentStatusSchema } from './enums.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);

// ───────────── Central media limits (shared by API validation and UI hints) ─────────────

/** Documents: files the knowledge pipeline accepts. Images are only accepted with a human-written description. */
export const KNOWLEDGE_DOCUMENT_MIME_TYPES = ['application/pdf', 'text/plain', 'text/markdown', 'image/jpeg', 'image/png', 'image/webp'] as const;
/** Shareable assets delivered to leads (WhatsApp-deliverable formats). */
export const KNOWLEDGE_ASSET_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'video/mp4'] as const;
/** Max characters of a text/FAQ document sent as JSON (larger text → upload a .txt/.md file). */
export const KNOWLEDGE_TEXT_MAX_CHARS = 100_000;

/** Route names that accept multipart/form-data (explicit allowlist for the API's JSON-only origin guard). */
export const MULTIPART_ROUTE_NAMES = ['uploadDocument', 'uploadDocumentVersion', 'uploadAsset', 'uploadAssetVersion', 'uploadContactImport', 'sendConversationVoiceNote'] as const;

export const KNOWLEDGE_DOCUMENT_KINDS = ['text', 'faq', 'pdf', 'image_description'] as const;
export const KnowledgeDocumentKindSchema = z.enum(KNOWLEDGE_DOCUMENT_KINDS);
export type KnowledgeDocumentKind = z.infer<typeof KnowledgeDocumentKindSchema>;
export const KNOWLEDGE_DOCUMENT_KIND_LABELS: Record<KnowledgeDocumentKind, string> = {
  text: 'Texto',
  faq: 'Preguntas frecuentes',
  pdf: 'PDF',
  image_description: 'Imagen con descripción',
};

export const ASSET_KINDS = ['pdf', 'image', 'video'] as const;
export const AssetKindSchema = z.enum(ASSET_KINDS);
export type AssetKind = z.infer<typeof AssetKindSchema>;
export const ASSET_KIND_LABELS: Record<AssetKind, string> = { pdf: 'PDF', image: 'Imagen', video: 'Video' };

export const ASSET_AUDIENCES = ['internal', 'shareable'] as const;
export const AssetAudienceSchema = z.enum(ASSET_AUDIENCES);
export type AssetAudience = z.infer<typeof AssetAudienceSchema>;
export const ASSET_AUDIENCE_LABELS: Record<AssetAudience, string> = { internal: 'Solo interno', shareable: 'Compartible con prospectos' };

export const ASSET_SEND_BLOCK_REASONS = ['internal', 'expired', 'superseded_version', 'wrong_project', 'archived', 'no_version'] as const;
export type AssetSendBlockReason = (typeof ASSET_SEND_BLOCK_REASONS)[number];
export const ASSET_SEND_BLOCK_LABELS: Record<AssetSendBlockReason, string> = {
  internal: 'Es material interno',
  expired: 'Está vencido',
  superseded_version: 'Es una versión reemplazada',
  wrong_project: 'Pertenece a otro proyecto',
  archived: 'Fue eliminado',
  no_version: 'No tiene archivo',
};

/** "all" = project + developer-wide (or everything visible when no project); "project" = only that project; "developer" = developer-wide only. */
export const KnowledgeScopeFilterSchema = z.enum(['all', 'project', 'developer']);

const boolParam = z.union([z.boolean(), z.enum(['true', 'false'])]).transform((v) => v === true || v === 'true');

// ───────────── Documents ─────────────

export const KnowledgeDocumentVersionSchema = z.object({
  id: Id,
  version: z.number().int(),
  status: DocumentStatusSchema,
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  hasFile: z.boolean(),
  chunkCount: z.number().int(),
  embeddedChunkCount: z.number().int(),
  error: z.string().nullable(),
  publishedAt: Iso.nullable(),
  createdAt: Iso,
});
export type KnowledgeDocumentVersion = z.infer<typeof KnowledgeDocumentVersionSchema>;

export const KnowledgeDocumentSchema = z.object({
  id: Id,
  projectId: Id.nullable(),
  title: z.string(),
  kind: KnowledgeDocumentKindSchema,
  language: z.string(),
  /** Status of the latest version. */
  status: DocumentStatusSchema,
  version: z.number().int(),
  /** Version the agent may currently use (null = none published). */
  publishedVersion: z.number().int().nullable(),
  /** Deleted: every version superseded and removed from retrieval (kept for audit). */
  archived: z.boolean(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type KnowledgeDocument = z.infer<typeof KnowledgeDocumentSchema>;

export const KnowledgeDocumentDetailSchema = KnowledgeDocumentSchema.extend({
  versions: z.array(KnowledgeDocumentVersionSchema),
  /** Extracted text preview of the latest version (bounded). */
  preview: z
    .object({
      versionId: Id,
      text: z.string(),
      totalChars: z.number().int(),
      truncated: z.boolean(),
    })
    .nullable(),
});
export type KnowledgeDocumentDetail = z.infer<typeof KnowledgeDocumentDetailSchema>;

export const DocumentListQuerySchema = z.object({
  projectId: Id.optional(),
  scope: KnowledgeScopeFilterSchema.default('all'),
  status: DocumentStatusSchema.optional(),
  includeArchived: boolParam.optional(),
});

export const FaqEntrySchema = z.object({ question: z.string().trim().min(1).max(500), answer: z.string().trim().min(1).max(5000) });
export type FaqEntry = z.infer<typeof FaqEntrySchema>;

const TextContent = z.string().trim().min(1, 'Escribe el contenido').max(KNOWLEDGE_TEXT_MAX_CHARS, `Máximo ${KNOWLEDGE_TEXT_MAX_CHARS.toLocaleString('es-MX')} caracteres`);
const Language = z.string().trim().min(2).max(10).default('es-MX');

/** Text / FAQ document from JSON (no file). */
export const CreateTextDocumentSchema = z
  .object({
    title: z.string().trim().min(1, 'Escribe un título').max(200),
    kind: z.enum(['text', 'faq']),
    projectId: Id.nullable().default(null),
    language: Language,
    content: TextContent.optional(),
    faqs: z.array(FaqEntrySchema).min(1).max(300).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'text' && !v.content) ctx.addIssue({ code: 'custom', path: ['content'], message: 'Escribe el contenido' });
    if (v.kind === 'faq' && !v.faqs?.length && !v.content) ctx.addIssue({ code: 'custom', path: ['faqs'], message: 'Agrega al menos una pregunta' });
  });

export const CreateTextVersionSchema = z
  .object({ content: TextContent.optional(), faqs: z.array(FaqEntrySchema).min(1).max(300).optional() })
  .refine((v) => Boolean(v.content || v.faqs?.length), { message: 'Escribe el contenido', path: ['content'] });

const emptyToUndefined = (v: unknown) => (v === '' || v === 'null' ? undefined : v);

/** multipart/form-data text fields of a file document upload (the file part is named "file"). */
export const UploadDocumentFieldsSchema = z.object({
  title: z.string().trim().min(1, 'Escribe un título').max(200),
  projectId: z.preprocess(emptyToUndefined, Id.optional()),
  language: z.preprocess(emptyToUndefined, z.string().trim().min(2).max(10).optional()),
  /** Required for images: the human-written, approved description (the system never pretends to read images). */
  description: z.preprocess(emptyToUndefined, z.string().trim().min(20, 'Describe la imagen con al menos 20 caracteres').max(20_000).optional()),
});

export const UploadVersionFieldsSchema = z.object({
  description: z.preprocess(emptyToUndefined, z.string().trim().min(20, 'Describe la imagen con al menos 20 caracteres').max(20_000).optional()),
});

export const RejectVersionSchema = z.object({ reason: z.string().trim().min(3, 'Explica el motivo').max(500) });

// ───────────── Retrieval ─────────────

export const KnowledgeSearchQuerySchema = z.object({
  q: z.string().trim().min(2, 'Escribe al menos 2 caracteres').max(500),
  projectId: Id.optional(),
  limit: z.coerce.number().int().min(1).max(10).default(5),
});

export const KnowledgePassageSchema = z.object({
  chunkId: Id,
  content: z.string(),
  documentId: Id,
  documentTitle: z.string(),
  documentKind: KnowledgeDocumentKindSchema,
  version: z.number().int(),
  page: z.number().int().nullable(),
  section: z.string().nullable(),
  projectId: Id.nullable(),
  score: z.number(),
  matchedBy: z.array(z.enum(['lexical', 'semantic'])),
});
export type KnowledgePassage = z.infer<typeof KnowledgePassageSchema>;

export const SemanticSearchStateSchema = z.enum(['enabled', 'disabled']);

export const KnowledgeSearchResultSchema = z.object({
  query: z.string(),
  projectId: Id.nullable(),
  semanticSearch: SemanticSearchStateSchema,
  /** What actually ran for this query (semantic may be enabled but fail/time out → lexical). */
  mode: z.enum(['hybrid', 'lexical']),
  passages: z.array(KnowledgePassageSchema),
  /** Set when the query asks for prices/availability/units: those always come from structured inventory tools, never from documents. */
  structuredHint: z.string().nullable(),
  tookMs: z.number().int(),
});
export type KnowledgeSearchResult = z.infer<typeof KnowledgeSearchResultSchema>;

export const KnowledgeCapabilitiesSchema = z.object({
  semanticSearch: SemanticSearchStateSchema,
  embeddingModel: z.string().nullable(),
  ocr: z.literal('unsupported'),
  storageDriver: z.enum(['filesystem', 's3']),
  maxUploadBytes: z.number().int(),
  documentMimeTypes: z.array(z.string()),
  assetMimeTypes: z.array(z.string()),
  maxPdfPages: z.number().int(),
  maxExtractedChars: z.number().int(),
});
export type KnowledgeCapabilities = z.infer<typeof KnowledgeCapabilitiesSchema>;

// ───────────── Assets ─────────────

export const AssetVersionSchema = z.object({
  id: Id,
  version: z.number().int(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  superseded: z.boolean(),
  createdAt: Iso,
});

export const AssetSchema = z.object({
  id: Id,
  projectId: Id.nullable(),
  title: z.string(),
  description: z.string().nullable(),
  kind: AssetKindSchema,
  audience: AssetAudienceSchema,
  language: z.string(),
  tags: z.array(z.string()),
  expiresAt: Iso.nullable(),
  expired: z.boolean(),
  archived: z.boolean(),
  currentVersion: AssetVersionSchema.nullable(),
  versionCount: z.number().int(),
  /** Result of the send policy for this asset in its own scope (the agent tool re-checks with the conversation's project). */
  sendable: z.object({ allowed: z.boolean(), reason: z.enum(ASSET_SEND_BLOCK_REASONS).nullable() }),
  createdAt: Iso,
  updatedAt: Iso,
});
export type Asset = z.infer<typeof AssetSchema>;

export const AssetListQuerySchema = z.object({
  projectId: Id.optional(),
  scope: KnowledgeScopeFilterSchema.default('all'),
  audience: AssetAudienceSchema.optional(),
  includeArchived: boolParam.optional(),
});

const tagsField = z.preprocess(
  (v) => {
    if (v === undefined || v === '') return undefined;
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t.startsWith('[')) {
        try {
          return JSON.parse(t) as unknown;
        } catch {
          return v;
        }
      }
      return t.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return v;
  },
  z.array(z.string().trim().min(1).max(40)).max(20).optional(),
);
const expiresField = z.preprocess(emptyToUndefined, z.iso.datetime({ offset: true }).optional());

/** multipart/form-data text fields of an asset upload (file part "file"). */
export const UploadAssetFieldsSchema = z.object({
  title: z.string().trim().min(1, 'Escribe un título').max(200),
  description: z.preprocess(emptyToUndefined, z.string().trim().max(2000).optional()),
  audience: AssetAudienceSchema.default('internal'),
  language: z.preprocess(emptyToUndefined, z.string().trim().min(2).max(10).optional()),
  projectId: z.preprocess(emptyToUndefined, Id.optional()),
  tags: tagsField,
  expiresAt: expiresField,
});

export const UpdateAssetSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  audience: AssetAudienceSchema.optional(),
  language: z.string().trim().min(2).max(10).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
});
